import React from 'react';
import { Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import JourneyPath from '../KPractice/shared/JourneyPath';

interface SelectionScreenProps {
  onStart: () => void;
  isLoading: boolean;
}

const SelectionScreen: React.FC<SelectionScreenProps> = ({ onStart, isLoading }) => {
  const { user } = useAuth();

  // Add keyboard event listener for Enter key
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isLoading) {
        onStart();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [onStart, isLoading]);

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
      {/* Journey Path - positioned at top of screen */}
      <div className="absolute top-6 left-0 right-0">
        <JourneyPath currentTrack={user?.focusTrack} />
      </div>

      <div className="max-w-4xl mx-auto space-y-8 pt-24">
        <h1 className="text-4xl font-bold text-center mb-6 animate-fade-in flex items-center justify-center gap-4">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
            Addition Accuracy Assessment
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6 p-6 bg-white rounded-lg shadow-md"
        >
          <div className="text-center space-y-6">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
              Instructions
            </h2>
            <ul className="space-y-3 text-base text-gray-600 max-w-2xl mx-auto text-left">
              <li className="flex items-center gap-4">
                <span className="font-bold text-blue-500 min-w-[1.5rem]">1.</span>
                <span>You will answer 20 addition questions</span>
              </li>
              <li className="flex items-center gap-4">
                <span className="font-bold text-blue-500 min-w-[1.5rem]">2.</span>
                <span>Take your time - there is no timer</span>
              </li>
              <li className="flex items-center gap-4">
                <span className="font-bold text-blue-500 min-w-[1.5rem]">3.</span>
                <span>You need 18 or more correct to pass</span>
              </li>
            </ul>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            onClick={onStart}
            disabled={isLoading}
            className={`w-full max-w-md mx-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500
              text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg
              transition-all duration-300 flex items-center justify-center gap-2
              ${isLoading && 'opacity-50 cursor-not-allowed'}`}
          >
            <span>{isLoading ? 'Loading...' : 'Start Assessment'}</span>
            {!isLoading && <Play className="w-5 h-5" />}
            {isLoading && (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default SelectionScreen;
